import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import User from "@ltypes/user";
import Button from "@components/forms/Button/Button";
import Card from "@components/ui/Card/Card";
import DisplayForm, { DisplayFormItemType } from "@components/forms/DisplayForm/DisplayForm";
import SceneTypeSelector from "@components/forms/SceneTypeSelector/SceneTypeSelector";
import SceneType from "@enums/sceneType";
import { useTranslation } from "react-i18next";
import Product from "@ltypes/product";

type CreateSceneProps = {
    productId: string|undefined,
    onCreate: (type: SceneType) => void,
};

function CreateScene(props: CreateSceneProps) {
    const createSceneRef = useRef<HTMLDivElement>(null);

    const user: User|null = useSelector((state: any) => state.user);
    
    const [creatingScene, setCreatingScene] = useState<boolean>(false);
    const [type, setType] = useState<number>(0);

    const { t } = useTranslation();

    const product: Product|undefined = props.productId === undefined ? undefined : user?.products.find((p: Product) => p.id === props.productId);

    const handleClickNewScene = () => {
        setCreatingScene(true);
    };

    const handleCreate = () => {
        props.onCreate(type);
        setCreatingScene(false);
    };

    useEffect(() => {
        if(!creatingScene)
            return;
        createSceneRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }, [creatingScene]);

    return (
        <div className="row">
            {!creatingScene || !product ? (
                <>
                    <div className="col-12 text-center d-none d-md-block">
                        <Button onClick={handleClickNewScene}>{t('page.scenes.create_new_scene')}</Button>
                    </div>
                    <div className="col-12 text-center d-block d-md-none">
                        <Button onClick={handleClickNewScene} filled>{t('page.scenes.create_new_scene')}</Button>
                    </div>
                </>
            ) : (
                <div ref={createSceneRef} className="col-12">
                    <h2 className="mb-1">{t('page.scenes.select_scene_type')}</h2>

                    <Card className="text-center">
                        {product.version.value < 2025.1 ? (
                            <DisplayForm className="justify-content-center" label="" value={type} items={[
                                {
                                    id: 0,
                                    background: 'scene-crypto',
                                },
                                {
                                    id: 1,
                                    background: 'scene-text',
                                },
                                {
                                    id: 2,
                                    background: 'scene-clock',
                                },
                                {
                                    id: 3,
                                    background: 'scene-weather',
                                },
                                {
                                    id: 4,
                                    background: 'scene-gif',
                                },
                                {
                                    id: 8,
                                    background: 'scene-spotify',
                                },
                                {
                                    id: 7,
                                    background: 'scene-off',
                                },
                            ].filter((item: DisplayFormItemType) => {
                                // Feature flipping
                                if(item.id === 8 && (product.version.value < 1.6)) {
                                    return false;
                                }
                                if(item.id === 4 && (product.version.value < 2.0)) {
                                    return false;
                                }
                                return true;
                            })} onChange={setType}/>
                        ) : (
                            <SceneTypeSelector className="justify-content-center" label="" value={type} product={product} onChange={setType}/>
                        )}

                        <div className="row mt-3 gx-2 justify-content-end">
                            <div className="col-auto">
                                <Button className="secondary" onClick={() => setCreatingScene(false)}>{t('text.cancel')}</Button>
                            </div>
                            <div className="col-auto">
                                <Button onClick={handleCreate}>{t('text.create')}</Button>
                            </div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
}

export default CreateScene;
